<template>
  <div ref="containerRef" class="org-tree-select">
    <!-- Trigger Button -->
    <button
      type="button"
      class="org-select-trigger"
      :class="{ active: isOpen, disabled }"
      :disabled="disabled"
      :title="disabled ? '当前账号仅授权单个店铺' : '点击切换店铺 / 组织'"
      @click="toggleDropdown"
    >
      <div class="trigger-left">
        <span class="store-icon-wrap" aria-hidden="true">
          <Globe v-if="session.storeId === 'all'" :size="14" class="store-icon" />
          <Store v-else :size="14" class="store-icon" />
        </span>
        <span class="trigger-label">
          <template v-if="currentStoreGroupName">
            <span class="group-prefix">{{ currentStoreGroupName }}</span>
            <span class="label-separator"> / </span>
          </template>
          <span class="store-name">{{ currentStore.name }}</span>
        </span>
      </div>
      <ChevronDown :size="14" class="chevron-icon" :class="{ open: isOpen }" />
    </button>

    <!-- Dropdown Panel -->
    <div v-if="isOpen" class="org-dropdown-panel">
      <!-- Search & Controls -->
      <div class="dropdown-header">
        <div class="search-box">
          <Search :size="13" class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索店铺、负责人或团队..."
            class="search-input"
            autofocus
            @click.stop
          />
          <button v-if="searchQuery" type="button" class="btn-clear" @click="searchQuery = ''">
            <X :size="12" />
          </button>
        </div>
        <div class="tree-controls">
          <button type="button" class="ctrl-btn" @click="toggleAllGroups">
            {{ allExpanded ? '收起全部' : '展开全部' }}
          </button>
        </div>
      </div>

      <!-- Quick Platform Option（仅 BOSS / SUPERADMIN 可见） -->
      <div
        v-if="canViewAllStores && (!searchQuery || '全平台'.includes(searchQuery))"
        class="platform-all-item"
        :class="{ selected: session.storeId === 'all' }"
        @click="selectStore('all')"
      >
        <div class="item-main">
          <Globe :size="14" class="globe-icon" />
          <span class="item-title">全平台所有店铺</span>
        </div>
        <Check v-if="session.storeId === 'all'" :size="14" class="check-icon" />
      </div>

      <!-- Tree Content -->
      <div class="tree-body">
        <div v-if="!filteredGroups.length" class="empty-search">
          <AlertCircle :size="16" />
          <span>未找到相关店铺</span>
        </div>

        <div
          v-for="group in filteredGroups"
          :key="group.id"
          class="tree-group-section"
        >
          <!-- Group Header -->
          <div
            class="group-header"
            @click="toggleGroup(group.id)"
          >
            <div class="group-title-row">
              <ChevronRight
                :size="13"
                class="group-arrow"
                :class="{ expanded: isGroupExpanded(group.id) }"
              />
              <Folder :size="14" class="folder-icon" />
              <span class="group-name">{{ group.name }}</span>
            </div>
            <span class="group-count">{{ group.stores.length }} 家店</span>
          </div>

          <!-- Group Stores List -->
          <div
            v-show="isGroupExpanded(group.id)"
            class="group-stores-list"
          >
            <div
              v-for="store in group.stores"
              :key="store.id"
              class="store-item"
              :class="{ selected: session.storeId === store.id }"
              @click="selectStore(store.id)"
            >
              <div class="store-item-info">
                <span class="store-dot"></span>
                <span class="store-name-text">{{ store.name }}</span>
              </div>
              <Check v-if="session.storeId === store.id" :size="14" class="check-icon" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import {
  Store,
  ChevronDown,
  ChevronRight,
  Search,
  X,
  Globe,
  Check,
  Folder,
  AlertCircle,
} from '@lucide/vue';
import { usePlatformStore } from '@/composables/usePlatformStore';

const props = defineProps({
  disabled: { type: Boolean, default: false },
});

const {
  session,
  user,
  roleKey,
  hasAllStoresAccess,
  currentStore,
  accessibleStores,
  organizationTree,
  switchStore,
  canViewAllStores,
} = usePlatformStore();

const isOpen = ref(false);
const searchQuery = ref('');
const containerRef = ref(null);
const expandedGroupIds = ref(new Set());

// 当前选中店铺所属分组名称
const currentStoreGroupName = computed(() => {
  if (session.storeId === 'all') return '';
  const store = accessibleStores.value.find(s => s.id === session.storeId);
  return store?.groupName || '';
});

// 构建结构化分组并过滤用户有权限访问的店铺
const accessibleGroups = computed(() => {
  const allowedStoreIds = new Set(accessibleStores.value.map(s => s.id));
  const groups = organizationTree.value?.groups || [];

  if (groups.length > 0) {
    return groups.map((g) => {
      const allowedStores = g.stores.filter(s => allowedStoreIds.has(s.id));
      return {
        ...g,
        stores: allowedStores,
      };
    }).filter(g => g.stores.length > 0);
  }

  // 兜底：若暂无树结构，按 accessibleStores 聚合
  return [{
    id: 'default',
    name: '店铺列表',
    stores: accessibleStores.value,
  }];
});

// 搜索过滤后的分组
const filteredGroups = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return accessibleGroups.value;

  return accessibleGroups.value.map((g) => {
    const groupMatches = g.name.toLowerCase().includes(q);
    const matchingStores = g.stores.filter(s =>
      s.name.toLowerCase().includes(q) || groupMatches
    );
    if (!matchingStores.length && !groupMatches) return null;
    return {
      ...g,
      stores: matchingStores.length ? matchingStores : g.stores,
    };
  }).filter(Boolean);
});

// 展开/折叠控制
function isGroupExpanded(groupId) {
  if (searchQuery.value.trim()) return true; // 搜索时自动展开
  return expandedGroupIds.value.has(groupId);
}

function toggleGroup(groupId) {
  if (expandedGroupIds.value.has(groupId)) {
    expandedGroupIds.value.delete(groupId);
  } else {
    expandedGroupIds.value.add(groupId);
  }
}

const allExpanded = computed(() => {
  return accessibleGroups.value.length > 0
    && accessibleGroups.value.every(g => expandedGroupIds.value.has(g.id));
});

function toggleAllGroups() {
  if (allExpanded.value) {
    expandedGroupIds.value.clear();
  } else {
    accessibleGroups.value.forEach(g => expandedGroupIds.value.add(g.id));
  }
}

// 默认展开当前选中店铺所在的分组
function expandCurrentStoreGroup() {
  if (session.storeId === 'all') {
    accessibleGroups.value.forEach(g => expandedGroupIds.value.add(g.id));
    return;
  }
  accessibleGroups.value.forEach((g) => {
    if (g.stores.some(s => s.id === session.storeId)) {
      expandedGroupIds.value.add(g.id);
    }
  });
}

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = '';
    expandCurrentStoreGroup();
  }
}

function selectStore(storeId) {
  switchStore(storeId);
  isOpen.value = false;
}

// 点击外部关闭
function handleClickOutside(e) {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    isOpen.value = false;
  }
}

watch(accessibleGroups, () => {
  expandCurrentStoreGroup();
}, { immediate: true });

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.org-tree-select {
  position: relative;
  display: inline-block;
}

.org-select-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 34px;
  padding: 4px 12px;
  background: #ffffff;
  border: 1px solid #c4b5fd;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  user-select: none;
  box-shadow: none;
}

.org-select-trigger:hover:not(.disabled) {
  border-color: #a78bfa;
  background: #ffffff;
}

.org-select-trigger.active {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
  background: #ffffff;
}

.org-select-trigger.disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.trigger-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  max-width: 280px;
}

.store-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(124, 58, 237, 0.08);
  flex-shrink: 0;
}

.store-icon {
  color: #7c3aed;
  flex-shrink: 0;
}

.trigger-label {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.group-prefix {
  color: #7c3aed;
  font-weight: 600;
  flex-shrink: 0;
}

.label-separator {
  color: #1e293b;
  font-weight: 500;
  flex-shrink: 0;
}

.store-name {
  color: #1e293b;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron-icon {
  color: #7c3aed;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.chevron-icon.open {
  transform: rotate(180deg);
  color: #7c3aed;
}

/* Dropdown Panel */
.org-dropdown-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 300px;
  max-height: 420px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(15, 23, 42, 0.04);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: dropdown-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes dropdown-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.dropdown-header {
  padding: 12px 12px 10px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.search-box {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #94a3b8;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 32px;
  padding: 0 28px 0 30px;
  font-size: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-input:focus {
  border-color: #c4b5fd;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}

.btn-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-clear:hover {
  color: #64748b;
}

.tree-controls {
  flex-shrink: 0;
}

.ctrl-btn {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  transition: color 0.15s ease;
}

.ctrl-btn:hover {
  color: #64748b;
  background: transparent;
}

.platform-all-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  margin: 4px 10px 2px;
  background: #f8fafc;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.platform-all-item:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.platform-all-item.selected {
  background: #f5f3ff;
  border-color: #ddd6fe;
  color: #6d28d9;
}

.item-main {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.platform-all-item.selected .item-main {
  color: #6d28d9;
}

.globe-icon {
  color: #7c3aed;
}

.platform-all-item.selected .globe-icon {
  color: #6d28d9;
}

.check-icon {
  color: #7c3aed;
  flex-shrink: 0;
}

.platform-all-item.selected .check-icon {
  color: #7c3aed;
}

/* Tree Body */
.tree-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px 8px 10px;
  max-height: 340px;
}

.tree-body::-webkit-scrollbar {
  width: 5px;
}

.tree-body::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 999px;
}

.tree-body::-webkit-scrollbar-track {
  background: transparent;
}

.empty-search {
  padding: 36px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.tree-group-section {
  margin-bottom: 4px;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s ease;
}

.group-header:hover {
  background: #f8fafc;
}

.group-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.group-arrow {
  color: #94a3b8;
  flex-shrink: 0;
  transition: transform 0.18s ease, color 0.18s ease;
}

.group-arrow.expanded {
  transform: rotate(90deg);
  color: #64748b;
}

.folder-icon {
  color: #f59e0b;
  flex-shrink: 0;
}

.group-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.group-count {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
  white-space: nowrap;
}

.group-stores-list {
  padding: 2px 0 4px 28px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.store-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.store-item:hover:not(.selected) {
  background: #f8fafc;
}

.store-item.selected {
  background: #f5f3ff;
}

.store-item-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.store-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cbd5e1;
  flex-shrink: 0;
}

.store-item.selected .store-dot {
  background: #7c3aed;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.18);
}

.store-name-text {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  line-height: 1.35;
}

.store-item.selected .store-name-text {
  color: #6d28d9;
  font-weight: 600;
}

.store-item.selected .check-icon {
  color: #7c3aed;
}
</style>
